import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Loader2 } from "lucide-react";

type F = {
  internal_id: string;
  scanner_name: string;
  level?: string | null;
  name?: string | null;
  description?: string | null;
};

type Snapshot = {
  id: string;
  source: string;
  git_sha: string | null;
  finding_count: number;
  new_count: number;
  created_at: string;
  findings: F[] | null;
};

const key = (f: F) => `${f.scanner_name}::${f.internal_id}`;

const FIELDS: Array<keyof F> = ["level", "name", "description"];

export default function AdminSecurityFindingDiff() {
  const { snapshotId } = useParams<{ snapshotId: string }>();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [previous, setPrevious] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!snapshotId) return;
      setLoading(true);
      const { data: curr } = await supabase
        .from("security_scan_snapshots")
        .select("id, source, git_sha, finding_count, new_count, created_at, findings")
        .eq("id", snapshotId)
        .maybeSingle();
      if (curr) {
        const { data: prev } = await supabase
          .from("security_scan_snapshots")
          .select("id, source, git_sha, finding_count, new_count, created_at, findings")
          .lt("created_at", curr.created_at)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        setSnapshot(curr as Snapshot);
        setPrevious((prev as Snapshot) ?? null);
      }
      setLoading(false);
    })();
  }, [snapshotId]);

  const diff = useMemo(() => {
    const cur = snapshot?.findings ?? [];
    const prev = previous?.findings ?? [];
    const curMap = new Map(cur.map((f) => [key(f), f]));
    const prevMap = new Map(prev.map((f) => [key(f), f]));

    const added = cur.filter((f) => !prevMap.has(key(f)));
    const removed = prev.filter((f) => !curMap.has(key(f)));
    const modified: Array<{ finding: F; changes: Array<{ field: string; from: string; to: string }> }> = [];
    for (const f of cur) {
      const p = prevMap.get(key(f));
      if (!p) continue;
      const changes: Array<{ field: string; from: string; to: string }> = [];
      for (const field of FIELDS) {
        const from = (p[field] ?? "") as string;
        const to = (f[field] ?? "") as string;
        if (from !== to) changes.push({ field: String(field), from, to });
      }
      if (changes.length) modified.push({ finding: f, changes });
    }
    return { added, removed, modified };
  }, [snapshot, previous]);

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Finding-level drilldown</h1>
          {snapshot && (
            <p className="text-xs text-muted-foreground mt-1 font-mono">
              {new Date(snapshot.created_at).toLocaleString()} · {snapshot.source}
              {snapshot.git_sha ? ` · ${snapshot.git_sha.slice(0, 7)}` : ""}
              {previous
                ? ` vs ${new Date(previous.created_at).toLocaleString()}`
                : " (no prior snapshot)"}
            </p>
          )}
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin/security-findings">
            <ChevronLeft className="size-4 mr-2" /> Back
          </Link>
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" /> Loading snapshot…
        </div>
      ) : !snapshot ? (
        <p className="text-sm text-muted-foreground">Snapshot not found.</p>
      ) : (
        <FilteredDiff diff={diff} />
      )}
    </div>
  );
}

function FilteredDiff({
  diff,
}: {
  diff: {
    added: F[];
    removed: F[];
    modified: Array<{ finding: F; changes: Array<{ field: string; from: string; to: string }> }>;
  };
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"id-asc" | "id-desc" | "scanner" | "level">("id-asc");

  const cmp = (a: F, b: F) => {
    if (sort === "id-asc") return a.internal_id.localeCompare(b.internal_id);
    if (sort === "id-desc") return b.internal_id.localeCompare(a.internal_id);
    if (sort === "scanner") return a.scanner_name.localeCompare(b.scanner_name);
    return (a.level ?? "").localeCompare(b.level ?? "");
  };

  const needle = q.trim().toLowerCase();
  const matchFinding = (f: F) =>
    !needle ||
    f.internal_id.toLowerCase().includes(needle) ||
    f.scanner_name.toLowerCase().includes(needle) ||
    (f.name ?? "").toLowerCase().includes(needle) ||
    (f.description ?? "").toLowerCase().includes(needle) ||
    (f.level ?? "").toLowerCase().includes(needle);

  const added = [...diff.added].filter(matchFinding).sort(cmp);
  const removed = [...diff.removed].filter(matchFinding).sort(cmp);
  const modified = [...diff.modified]
    .filter(({ finding, changes }) => {
      if (!needle) return true;
      if (matchFinding(finding)) return true;
      return changes.some(
        (c) =>
          c.field.toLowerCase().includes(needle) ||
          c.from.toLowerCase().includes(needle) ||
          c.to.toLowerCase().includes(needle),
      );
    })
    .sort((a, b) => cmp(a.finding, b.finding));

  return (
    <>
      <div className="grid grid-cols-3 gap-3 text-center">
        <Stat label="Added" value={added.length} tone="destructive" />
        <Stat label="Removed" value={removed.length} tone="primary" />
        <Stat label="Modified" value={modified.length} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search finding id / field name / changed value"
          className="h-9 flex-1 min-w-[220px] font-mono text-xs"
        />
        <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
          <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="id-asc">Sort: ID ↑</SelectItem>
            <SelectItem value="id-desc">Sort: ID ↓</SelectItem>
            <SelectItem value="scanner">Sort: scanner</SelectItem>
            <SelectItem value="level">Sort: level</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Section title="Added" tone="destructive">
        {added.map((f) => (
          <FindingCard key={key(f)} finding={f} fields={FIELDS.map((field) => ({ field: String(field), value: (f[field] ?? "") as string }))} />
        ))}
      </Section>
      <Section title="Removed" tone="primary">
        {removed.map((f) => (
          <FindingCard key={key(f)} finding={f} fields={FIELDS.map((field) => ({ field: String(field), value: (f[field] ?? "") as string }))} />
        ))}
      </Section>
      <Section title="Modified">
        {modified.map(({ finding, changes }) => (
          <div key={key(finding)} className="rounded border border-border p-3 mb-2">
            <div className="font-mono text-xs break-all">{finding.internal_id}</div>
            <div className="font-mono text-[10px] text-muted-foreground mb-2">
              {finding.scanner_name}
              {finding.level ? ` · ${finding.level}` : ""}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground font-mono text-[10px] uppercase">
                  <th className="py-1 pr-3 w-24">Field</th>
                  <th className="py-1 pr-3">Before</th>
                  <th className="py-1">After</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.field} className="border-t border-border/60 align-top">
                    <td className="py-1 pr-3 font-mono">{c.field}</td>
                    <td className="py-1 pr-3 text-destructive font-mono whitespace-pre-wrap break-words">{c.from || "—"}</td>
                    <td className="py-1 text-primary font-mono whitespace-pre-wrap break-words">{c.to || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Section>

      {added.length + removed.length + modified.length === 0 && (
        <p className="text-sm text-muted-foreground">No field-level changes match this search.</p>
      )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "destructive" | "primary" }) {
  const cls = tone === "destructive" ? "text-destructive border-destructive/40" : tone === "primary" ? "text-primary border-primary/40" : "border-border";
  return (
    <div className={`rounded border p-3 ${cls}`}>
      <div className="font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl font-bold ${tone ? "" : ""}`}>{value}</div>
    </div>
  );
}

function Section({ title, tone, children }: { title: string; tone?: "destructive" | "primary"; children: React.ReactNode }) {
  const hasContent = Array.isArray(children) ? children.some(Boolean) : !!children;
  if (!hasContent) return null;
  const toneClass = tone === "destructive" ? "text-destructive" : tone === "primary" ? "text-primary" : "text-muted-foreground";
  return (
    <div>
      <h2 className={`font-display text-sm font-bold uppercase mb-2 ${toneClass}`}>{title}</h2>
      <div>{children}</div>
    </div>
  );
}

function FindingCard({ finding, fields }: { finding: F; fields: Array<{ field: string; value: string }> }) {
  return (
    <div className="rounded border border-border p-3 mb-2 text-xs">
      <div className="font-mono break-all">{finding.internal_id}</div>
      <div className="font-mono text-[10px] text-muted-foreground mb-2">
        {finding.scanner_name}
        {finding.level ? ` · ${finding.level}` : ""}
      </div>
      <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1">
        {fields.map((f) => (
          <div key={f.field} className="contents">
            <dt className="font-mono text-[10px] uppercase text-muted-foreground">{f.field}</dt>
            <dd className="font-mono whitespace-pre-wrap break-words">{f.value || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
